import React, { useContext, useState } from 'react';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { ContainerTypeContext } from '../../../../../../context/ContainerTypeContext';
import {
  ContainerTypeMutations,
  CreateContainerTypeResponse,
  CreateContainerTypeVariables,
  DeleteContainerTypeResponse,
  DeleteContainerTypeVariables,
  UpdateContainerTypeResponse,
  UpdateContainerTypeVariables,
} from '../../../../../../graphql/containerType.graphql';
import useMutation from '../../../../../../hooks/useMutation';
import { useToast } from '../../../../../../providers/ToastProvider';
import { ContainerTypeInput } from '../../../../../../types/container_type';
import { ContainerType } from '../../../../../../types/container_type/containerType';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import { Button, CancelButton, DeleteButton } from '../../../../../components/Button';
import { ActionModal } from '../../../../../components/Modal';
import { TextInput } from '../../../../../components/TextInput';
import { ToastType } from '../../../../../components/Toast';

interface ContainerTypeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  containerType: ContainerType | undefined;
}

export default function ContainerTypeModal({ open, setOpen, containerType }: ContainerTypeModalProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { containerTypes, setContainerTypes } = useContext(ContainerTypeContext);

  const [input, setInput] = useState(new ContainerTypeInput(containerType || { companyId: currentCompany.id }));

  const handleActionComplete = (containerType: ContainerType) => {
    setContainerTypes(containerTypes.set(containerType.id, containerType));
    setOpen(false);
  };

  const [create, { loading: createLoading }] = useMutation<CreateContainerTypeResponse, CreateContainerTypeVariables>(
    ContainerTypeMutations.create,
    {
      variables: {
        containerTypes: [input.forCreate()],
      },
      onCompleted: response => {
        handleActionComplete(response.createContainerType[0]);
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.containerTypeCreatedSuccessfullyToastTitle).singular.upper,
        });
      },
    },
  );

  const [update, { loading: updateLoading }] = useMutation<UpdateContainerTypeResponse, UpdateContainerTypeVariables>(
    ContainerTypeMutations.update,
    {
      variables: {
        containerTypes: [input.forCreate()],
      },
      onCompleted: response => {
        handleActionComplete(response.updateContainerType[0]);
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.containerTypeUpdatedSuccessfullyToastTitle).singular.upper,
        });
      },
    },
  );

  const [remove, { loading: deleteLoading }] = useMutation<DeleteContainerTypeResponse, DeleteContainerTypeVariables>(
    ContainerTypeMutations.remove,
    {
      variables: { containerTypes: [input.forDelete()] },
      onCompleted: response => {
        response.deleteContainerType.forEach(ct => containerTypes.delete(ct.id));
        setContainerTypes(new Map(containerTypes));
        setOpen(false);
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.containerTypeDeletedSuccessfullyToastTitle).singular.upper,
        });
      },
    },
  );

  const loading = createLoading || updateLoading || deleteLoading;

  const handleSave = () => {
    if (containerType) {
      update();
    } else {
      create();
    }
  };

  const footer = (
    <div className='flex items-center justify-between gap-2'>
      {containerType ? (
        <DeleteButton onClick={() => remove()} loading={deleteLoading} disabled={createLoading || updateLoading} />
      ) : (
        <div></div>
      )}
      <div className='flex gap-2'>
        <CancelButton onClick={() => setOpen(false)} disabled={loading} />
        <Button
          text={t(containerType ? TranslationKey.update : TranslationKey.create).singular.upper}
          onClick={handleSave}
          loading={loading}
          disabled={deleteLoading}
        />
      </div>
    </div>
  );

  return (
    <ActionModal
      open={open}
      setOpen={setOpen}
      title={t(containerType ? TranslationKey.updateContainerType : TranslationKey.createContainerType).singular.upper}
      footer={footer}
    >
      <div className='grid gap-4'>
        <TextInput
          mandatory
          value={input.name}
          onChange={value => setInput(input.withName(value))}
          label={t(TranslationKey.name).singular.upper}
          placeholder={t(TranslationKey.containerTypeNameInputPlaceholder).singular.upper}
        />
      </div>
    </ActionModal>
  );
}
